/**
 * Registro de "hay cambios sin guardar" del shell.
 *
 * ## Por qué un registro y no `useBlocker`
 *
 * React Router trae `useBlocker` para exactamente esto, y **aquí no se puede
 * usar**: solo funciona con el data router (`createBrowserRouter`), y el ERP
 * monta `BrowserRouter` en `src/pages/index.jsx`. Migrar el router arrastraría
 * las 174 rutas y sus guards, que es un cambio de otro tamaño y de otro riesgo.
 *
 * Así que el shell intercepta la navegación por su cuenta, y necesita poder
 * preguntar "¿hay algo sin guardar?" sin re-renderizarse cada vez que el usuario
 * teclea una letra en un formulario. De ahí un registro de funciones en vez de
 * un contexto con estado: los formularios registran un lector, y el Layout lo
 * consulta en el momento del click.
 *
 * ## Por qué importaba
 *
 * Doce formularios rastreaban su estado sucio (`dirty` / `isDirty`) y **solo uno
 * avisaba** —`useConfiguracion`, y solo al cerrar la pestaña—. En el formulario
 * de factura el dato existía y únicamente lo consultaba su propio botón
 * "Cancelar": pulsar cualquier entrada del menú lateral perdía la factura entera
 * en silencio.
 */

/** @type {Set<() => boolean>} */
const guards = new Set();

/**
 * Registra un lector de "estoy sucio". Devuelve la función para darse de baja.
 * @param {() => boolean} isDirty
 * @returns {() => void}
 */
export function registerUnsavedGuard(isDirty) {
  guards.add(isDirty);
  return () => { guards.delete(isDirty); };
}

/** ¿Algún formulario montado tiene cambios sin guardar? */
export function hasUnsavedChanges() {
  for (const isDirty of guards) {
    try {
      if (isDirty()) return true;
    } catch {
      // Un lector que revienta no puede bloquear la navegación del usuario:
      // dejarlo pasar es preferible a dejarlo encerrado en una pantalla.
    }
  }
  return false;
}

/**
 * Vacía el registro. Para tests: es estado de módulo y sobrevive entre casos,
 * así que un guard olvidado haría fallar al siguiente por un motivo invisible.
 */
export function __resetUnsavedGuards() {
  guards.clear();
}

/** Cuántos guards hay registrados. Solo para tests y depuración. */
export function __unsavedGuardCount() {
  return guards.size;
}
